import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import type { PermissionModule, UserRole } from '@/types'
import {
  canModuleAction,
  type ModuleAction,
} from '@/lib/permissions'
import { firstAccessiblePath } from '@/config/moduleRoutes'

interface ModuleRouteProps {
  children: ReactNode
  module: PermissionModule
  /** Minimum module ability required (default: view). */
  action?: ModuleAction
  /** When set, user must also hold one of these roles (portal shell). */
  allowedRoles?: UserRole[]
  redirectTo?: string
}

/**
 * Route guard that requires a module ability (default `{module}.view`) and
 * optional role membership. Users without access are redirected to their
 * first accessible module.
 */
export function ModuleRoute({
  children,
  module,
  action = 'view',
  allowedRoles,
  redirectTo = '/login',
}: ModuleRouteProps) {
  const { isAuthenticated, user } = useAppSelector(s => s.auth)
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  if (allowedRoles && user) {
    const userRoles =
      user.roles && user.roles.length ? user.roles : [user.role]
    if (!userRoles.some(r => r && allowedRoles.includes(r))) {
      return (
        <Navigate to={firstAccessiblePath(user.permissions)} replace />
      )
    }
  }

  if (!canModuleAction(user?.permissions, module, action)) {
    return (
      <Navigate to={firstAccessiblePath(user?.permissions)} replace />
    )
  }

  return <>{children}</>
}

export default ModuleRoute
